/**
 * @file legal-usage-lines.ts
 * @description Строки окон «Снять с публикации» и «Удалить» по `usage`
 * документа (phase19.16, SB16, SB125, SB143; uiux-legal-steps-b-c §9).
 *
 * Чистые функции: окно только рисует строки, правила проверяются тестом без
 * разметки. Источники строк — `usage.menuItems[]` (пункты меню шапки и
 * подвала), `usage.footerRows` (свои строки блока «Ссылки» подвала) и
 * `usage.group`. Кнопки секций сервер пока не считает (TD-484) — про них
 * одна общая строка при снятии.
 */

import { isLegalTextOnlySlug, type LegalDocumentDto, type SiteMenuLocation } from "@chaiberi/shared";

/** Где стоит пункт меню — по-русски. */
const MENU_LOCATION_TEXT: Readonly<Record<SiteMenuLocation, string>> = {
  HEADER: "меню шапки",
  FOOTER: "меню подвала",
};

/** Поля документа, нужные окнам. */
export type LegalUsageDoc = Pick<LegalDocumentDto, "slug" | "kind" | "title" | "usage" | "live" | "groupSource" | "isPublished" | "hasDraftChanges">;

/** Сколько редакций у документа: номер действующей = их число (номера идут подряд). */
export function legalRevisionCount(doc: Pick<LegalDocumentDto, "live">): number {
  return doc.live?.number ?? 0;
}

/** Обязательный документ, без которого висит плашка SB125 (оферта, политика ПДн). */
export function isRequiredLegalDoc(doc: Pick<LegalDocumentDto, "slug">): boolean {
  return isLegalTextOnlySlug(doc.slug);
}

/** Красная полоса окна снятия у оферты и политики (вопрос 11 §12 — опасная кнопка, без галочки). */
export function requiredDocUnpublishWarning(doc: Pick<LegalDocumentDto, "slug">): string {
  // Вызывается только для оферты и политики (`isRequiredLegalDoc`).
  const label = doc.slug === "offer" ? "оферты" : "политики ПДн";
  return (
    `Это обязательный документ. Пока его нет, в кабинете будет плашка «Сайт работает без: ${label}», ` +
    "а покупатели при оформлении не увидят его текста. Оформлять заказы они смогут."
  );
}

/** Пункты меню: «Пункт меню подвала «Хранение» скроется». */
function menuLines(doc: LegalUsageDoc, tail: string): string[] {
  return doc.usage.menuItems.map((item) => `Пункт ${MENU_LOCATION_TEXT[item.location]} «${item.label}» ${tail}`);
}

/** Есть ли своя строка в «Ссылках» подвала (в черновике оформления или на сайте). */
function hasFooterRow(doc: LegalUsageDoc): boolean {
  return doc.usage.footerRows.draft || doc.usage.footerRows.published;
}

/** «Где используется» в карточке: пункты меню, строка «Ссылок» подвала, группа. */
export function usageSummary(doc: LegalUsageDoc): string[] {
  const lines = doc.usage.menuItems.map((item) => `Пункт ${MENU_LOCATION_TEXT[item.location]} «${item.label}»`);
  if (hasFooterRow(doc)) {
    const where = doc.usage.footerRows.published ? "на сайте" : "в черновике оформления";
    lines.push(`Строка в блоке «Ссылки» подвала (${where})`);
  }
  if (doc.usage.group) lines.push(`Группа «${doc.usage.group.title}»`);
  return lines;
}

/** «Что изменится» при снятии с публикации. */
export function unpublishConsequences(doc: LegalUsageDoc): string[] {
  const lines: string[] = [];
  if (doc.kind === "GROUP") {
    lines.push(`Страница «${doc.title}» пропадёт с сайта. Документы в ней остаются опубликованными по своим адресам.`);
  }
  lines.push(...menuLines(doc, "скроется"));
  if (hasFooterRow(doc)) lines.push("Строка в блоке «Ссылки» подвала скроется");
  if (doc.usage.group) lines.push(`В группе «${doc.usage.group.title}» документа не будет`);
  lines.push("Кнопки в секциях, ведущие на документ, скроются");
  return lines;
}

/** Подвал окна снятия: что будет с редакциями и как вернуть. */
export function unpublishRevisionNote(doc: LegalUsageDoc): string | null {
  const count = legalRevisionCount(doc);
  if (doc.kind === "GROUP" || count === 0) return null;
  const kept = `Прежние редакции (${count}) останутся доступны по прямым ссылкам — покупатели прежних заказов смогут открыть свои условия. `;
  // С правками черновика «Показать снова» не будет: вернуть можно только новой редакцией.
  return doc.hasDraftChanges
    ? `${kept}В черновике есть правки — чтобы вернуть документ на сайт, понадобится «Сохранить и опубликовать» (появится редакция ${count + 1}).`
    : `${kept}Вернуть можно кнопкой «Показать снова» — та же редакция ${count}, без новой публикации.`;
}

/** Можно ли удалить: TEXT/PDF с редакциями — нет (SB143), группы и черновики — да. */
export function canDeleteLegalDoc(doc: Pick<LegalDocumentDto, "kind" | "live">): boolean {
  return doc.kind === "GROUP" || legalRevisionCount(doc) === 0;
}

/** Текст окна-объяснения «Этот документ нельзя удалить». */
export function deleteBlockedText(doc: Pick<LegalDocumentDto, "live">): string {
  return (
    `У него есть опубликованные редакции (${legalRevisionCount(doc)}). Их хранят не меньше 3 лет — это доказательство условий прежних заказов. ` +
    "Чтобы убрать документ с сайта, снимите его с публикации."
  );
}

/** Вопрос и пояснение окна удаления по виду документа. */
export function deleteIntro(doc: LegalUsageDoc): { title: string; text: string; action: string } {
  if (doc.kind === "GROUP" && doc.groupSource === "PRODUCER_CERTIFICATES") {
    return {
      title: `Удалить группу «${doc.title}»?`,
      text: `Сертификаты в карточках производителей не удаляются. Страница «${doc.title}» перестанет открываться. Создать её снова можно в «Новый документ».`,
      action: "Удалить группу",
    };
  }
  if (doc.kind === "GROUP") {
    return {
      title: `Удалить группу «${doc.title}»?`,
      text: "Документы в ней не удаляются: они станут документами без группы и останутся на сайте по своим адресам. Страница группы перестанет открываться.",
      action: "Удалить группу",
    };
  }
  return {
    title: `Удалить черновик «${doc.title}»?`,
    text: "Его ещё не публиковали — на сайте ничего не изменится.",
    action: "Удалить",
  };
}

/** «Что изменится» при удалении: ссылки останутся без цели — их нужно убрать. */
export function deleteConsequences(doc: LegalUsageDoc): string[] {
  const lines = menuLines(doc, "останется без цели — уберите его в меню");
  if (hasFooterRow(doc)) lines.push("Строка в «Ссылках» подвала останется без цели и скроется на сайте — уберите её");
  if (doc.usage.group) lines.push(`Документ есть в группе «${doc.usage.group.title}» — после удаления он исчезнет оттуда`);
  return lines;
}
